import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

// API hook for the issue tracking feature ("Mängel & Störungen"): kanban board,
// reporting (incl. QR flow), photos, escalation audit, specialty categories,
// IT statistics and the to-do bridge.
const useIssue = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const request = useCallback(
    async ({ url, method = 'GET', body, toastMessage, silentCodes = [] }) => {
      try {
        return await sendRequest({
          url,
          method,
          body,
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        // Codes the caller handles with a specific toast get no generic one.
        if (toastMessage && !silentCodes.includes(err?.code)) toast.error(toastMessage);
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getIssues = useCallback(
    async (params = {}) => {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      ).toString();
      return request({
        url: `/api/v1/issue${query ? `?${query}` : ''}`,
        toastMessage: 'Fehler beim Laden der Issues',
      });
    },
    [request]
  );

  const getIssueById = useCallback(
    async issueId =>
      request({ url: `/api/v1/issue/${issueId}`, toastMessage: 'Fehler beim Laden des Issues' }),
    [request]
  );

  const createIssue = useCallback(
    async payload =>
      request({
        url: '/api/v1/issue',
        method: 'POST',
        body: payload,
        toastMessage: 'Fehler beim Melden des Issues',
      }),
    [request]
  );

  const updateIssue = useCallback(
    async (issueId, payload) =>
      request({
        url: `/api/v1/issue/${issueId}`,
        method: 'PATCH',
        body: payload,
        toastMessage: 'Fehler beim Aktualisieren des Issues',
      }),
    [request]
  );

  const acknowledgeIssue = useCallback(
    async (issueId, assignedUserId = null) =>
      request({
        url: `/api/v1/issue/${issueId}/acknowledge`,
        method: 'POST',
        body: assignedUserId ? { assignedUserId } : {},
        toastMessage: 'Fehler beim Reagieren auf das Issue',
      }),
    [request]
  );

  const assignIssue = useCallback(
    async (issueId, assignedUserId) =>
      request({
        url: `/api/v1/issue/${issueId}/assign`,
        method: 'POST',
        body: { assignedUserId },
        toastMessage: 'Fehler beim Zuweisen des Issues',
      }),
    [request]
  );

  const changeIssueStatus = useCallback(
    async (issueId, status, resolutionNote = null) =>
      request({
        url: `/api/v1/issue/${issueId}/status`,
        method: 'POST',
        body: { status, ...(resolutionNote ? { resolutionNote } : {}) },
        toastMessage: 'Fehler beim Ändern des Status',
      }),
    [request]
  );

  // Hands a ticket that is really a software topic over to the developer
  // board: creates a dev todo and resolves the issue with a note in one step.
  const forwardToDevTodo = useCallback(
    async (issueId, { priority, title, resolutionNote } = {}) =>
      request({
        url: `/api/v1/issue/${issueId}/forwardToDevTodo`,
        method: 'POST',
        body: {
          ...(priority ? { priority } : {}),
          ...(title ? { title } : {}),
          ...(resolutionNote ? { resolutionNote } : {}),
        },
        toastMessage: 'Fehler beim Übergeben an die Entwicklung',
      }),
    [request]
  );

  // `internal: true` stores an IT-only note (requires issue:internalNote).
  const addComment = useCallback(
    async (issueId, text, internal = false) =>
      request({
        url: `/api/v1/issue/${issueId}/comment`,
        method: 'POST',
        body: { text, ...(internal ? { internal: true } : {}) },
        toastMessage: internal
          ? 'Fehler beim Speichern der internen Notiz'
          : 'Fehler beim Speichern des Kommentars',
      }),
    [request]
  );

  const uploadPhoto = useCallback(
    async (issueId, { fileName, contentType, dataBase64 }) =>
      request({
        url: `/api/v1/issue/${issueId}/photo`,
        method: 'POST',
        body: { fileName, contentType, dataBase64 },
        toastMessage: 'Fehler beim Hochladen des Fotos',
      }),
    [request]
  );

  const getPhotoUrl = useCallback(
    (issueId, photoId) => `${import.meta.env.VITE_BE_URL}/api/v1/issue/${issueId}/photo/${photoId}`,
    []
  );

  // Photos require the auth header, so they are fetched as blobs.
  const fetchPhotoBlob = useCallback(
    async (issueId, photoId) => {
      try {
        return await sendRequest({
          url: `/api/v1/issue/${issueId}/photo/${photoId}`,
          headers: authCtx.getAuthHeaders(),
          returnBlob: true,
        });
      } catch (err) {
        toast.error('Fehler beim Laden des Fotos');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const addToMyTodos = useCallback(
    async issueId =>
      request({
        url: `/api/v1/issue/${issueId}/addToMyTodos`,
        method: 'POST',
        body: {},
        toastMessage: 'Fehler beim Übernehmen in die persönlichen Aufgaben',
        silentCodes: ['TODO_TOOL_NOT_AVAILABLE'],
      }),
    [request]
  );

  const getStatusOverview = useCallback(
    async () =>
      request({
        url: '/api/v1/issue/statusOverview',
        toastMessage: 'Fehler beim Laden der Raum-Übersicht',
      }),
    [request]
  );

  const getEscalationRuns = useCallback(
    async () =>
      request({
        url: '/api/v1/issue/escalation',
        toastMessage: 'Fehler beim Laden des Eskalations-Audits',
      }),
    [request]
  );

  const getStatistics = useCallback(
    async (params = {}) => {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      ).toString();
      return request({
        url: `/api/v1/issue/statistics${query ? `?${query}` : ''}`,
        toastMessage: 'Fehler beim Laden der IT-Statistik',
      });
    },
    [request]
  );

  return {
    isLoading,
    error,
    getIssues,
    getIssueById,
    createIssue,
    updateIssue,
    acknowledgeIssue,
    assignIssue,
    changeIssueStatus,
    forwardToDevTodo,
    addComment,
    uploadPhoto,
    getPhotoUrl,
    fetchPhotoBlob,
    addToMyTodos,
    getStatusOverview,
    getEscalationRuns,
    getStatistics,
  };
};

export default useIssue;
